import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  IconButton,
  Chip,
  Paper
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

/**
 * ResumeDraftModal Component
 * 
 * Shows modal with list of available drafts, allowing user to:
 * - Resume a draft (load saved state)
 * - Discard a specific draft (delete)
 * - Cancel (close modal, keep drafts)
 * 
 * Usage:
 * <ResumeDraftModal
 *   drafts={[draft1, draft2, ...]}
 *   onResume={(draft) => handleResumeDraft(draft)}
 *   onDiscard={(draftId) => handleDiscardDraft(draftId)}
 *   onCancel={handleCancelDraft}
 * />
 */
const ResumeDraftModal = ({ drafts = [], onResume, onDiscard, onCancel }) => {
  if (!drafts || drafts.length === 0) return null;

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Bilinmeyen tarih';
    const date = new Date(timestamp);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get draft summary
  const getDraftSummary = (draft) => {
    const title = draft.template?.title && draft.template.title.trim() !== ''
      ? draft.template.title.trim()
      : 'Başlıksız Taslak';
    
    const contentSnippet = draft.template?.contentHtml
      ? draft.template.contentHtml.replace(/<[^>]*>/g, '').substring(0, 100) + '...'
      : 'İçerik yok';
    
    const questionsCount = draft.questions?.length || 0;
    
    return { title, contentSnippet, questionsCount };
  };

  return (
    <Dialog
      open={drafts.length > 0}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="primary" />
          <Typography variant="h6" component="span">
            Kaydedilmemiş Taslaklar ({drafts.length})
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Daha önce kaydedilmemiş {drafts.length} taslak bulundu. Devam etmek istediğiniz taslağı seçin.
        </Alert>

        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {drafts.map((draft, index) => {
            const { title, contentSnippet, questionsCount } = getDraftSummary(draft);
            
            return (
              <React.Fragment key={draft.id || index}>
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Draft Number */}
                    <Box
                      sx={{
                        minWidth: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}
                    >
                      {index + 1}
                    </Box>

                    {/* Draft Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {title}
                      </Typography>
                      
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {contentSnippet}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {questionsCount > 0 && (
                          <Chip
                            label={`${questionsCount} soru`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(draft.timestamp)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<RestoreIcon />}
                        onClick={() => onResume(draft)}
                      >
                        Devam Et
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDiscard(draft.id)}
                        aria-label="Taslağı sil"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </React.Fragment>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          startIcon={<CancelIcon />}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={() => onDiscard(null)}
          startIcon={<DeleteIcon />}
          color="error"
          variant="outlined"
        >
          Tümünü Sil
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ResumeDraftModal.propTypes = {
  drafts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        category: PropTypes.string,
        contentHtml: PropTypes.string,
        thumbnail: PropTypes.string,
        isPublic: PropTypes.bool
      }),
      questions: PropTypes.array,
      timestamp: PropTypes.number
    })
  ),
  onResume: PropTypes.func.isRequired, // (draft) => void
  onDiscard: PropTypes.func.isRequired, // (draftId) => void - if null, delete all
  onCancel: PropTypes.func.isRequired
};

ResumeDraftModal.defaultProps = {
  drafts: []
};

export default ResumeDraftModal;

